import React from 'react';
import * as Yup from 'yup';
import { Button, Group, Tabs } from '@economic/taco';
import { useCurrentAgreement } from '@economic/react-app';

import { t, Trans } from '@lingui/macro';
import useQuery from '~/app/shared/hooks/useQuery';
import DimensionLineCombobox from '~/app/shared/dimensions/components/DimensionLineCombobox';
import { AccountMultipleDimension } from '~/types/internal-api';
import UnitDropdown from '~/app/shared/components/ui/UnitDropdown';
import useDimensionsAccessLevel from '~/app/shared/dimensions/hooks/useDimensionsAccessLevel';
import { Dimension, DimensionsAccess } from '~/app/shared/dimensions/types';
import { Column, Row } from '../../shared/components/Grid/Grid';
import { Condition, Field, Form } from '../../shared/components/Form';
import AccessDropdown from '../../shared/components/ui/AccessDropdown';
import AccountComboBox from '../../shared/components/ui/AccountCombobox';
import AccountTypeDropdown from '../../shared/components/ui/AccountTypeDropdown';
import DebitCreditDropdown from '../../shared/components/ui/DebitCreditDropdown';
import VatCodeDropdown from '../../shared/components/ui/VatCodeDropdown';
import { FormProps } from '../types';
import internalApiClient from '../../shared/util/api/internalApiClient';
import KeyFigureCodeDropdown from '../../shared/components/ui/KeyFigureCodeDropdown';
import { t as tLegacy } from '../../shared/components/l10n/l10n';
import DepartmentCombobox from '../../shared/components/ui/DepartmentCombobox';
import CurrencyDropdown from '../../shared/components/ui/CurrencyDropdown';

const isFieldDisabled = (name, values) => {
    switch (name) {
        case 'vatCode':
        case 'accounts.contraAccount':
        case 'accounts.realisationAccount':
        case 'debitCredit':
        case 'access':
        case 'isBlockedForDirectEntries':
        case 'currency':
            return values.type !== 1 && values.type !== 2;

        case 'accounts.openingAccount':
            return values.type !== 2;

        case 'accounts.totalFromAccount':
            return values.type !== 3;

        case 'keyFigureCode':
            return values.type !== 1 && values.type !== 2 && values.type !== 3 && values.type !== 6;

        case 'totalIntervals':
            return values.type !== 6;

        default:
            return false;
    }
};

interface Account {
    type: number;
    number: number;
    name: string;
    vatCode?: string;
    accounts?: {
        contraAccount?: number;
        totalFromAccount?: number;
        openingAccount?: number;
        realisationAccount?: number;
    };
    keyFigureCode?: number;
    debitCredit: boolean;
    access: boolean;
    isBlockedForDirectEntries: boolean;
    totalIntervals?: string;
    dimension?: {
        department?: number;
        isDepartmentMandatory?: boolean;
        isUnitMandatory?: boolean;
        unit1?: number;
        unit2?: number;
    };
    currency?: string;
    dimensions?: AccountMultipleDimension[];
}

const initialValues: Partial<Account> = {
    type: undefined,
    number: undefined,
    name: undefined,
    vatCode: undefined,
    accounts: {
        contraAccount: undefined,
        totalFromAccount: undefined,
        openingAccount: undefined,
        realisationAccount: undefined,
    },
    keyFigureCode: undefined,
    debitCredit: false,
    access: true,
    isBlockedForDirectEntries: false,
    totalIntervals: undefined,
    dimension: {
        department: undefined,
        isDepartmentMandatory: false,
        isUnitMandatory: false,
        unit1: undefined,
        unit2: undefined,
    },
    currency: undefined,
};

const validationSchema = Yup.object({
    type: Yup.number().required(),
    number: Yup.number().required(),
    name: Yup.string().required(),
    vatCode: Yup.string().nullable(),
    accounts: Yup.object({
        contraAccount: Yup.number().nullable(),
        totalFromAccount: Yup.number().nullable(),
        openingAccount: Yup.number().nullable(),
        realisationAccount: Yup.number().nullable(),
    }).nullable(),
    keyFigureCode: Yup.number().nullable(),
    debitCredit: Yup.boolean().required(),
    access: Yup.boolean().required(),
    isBlockedForDirectEntries: Yup.boolean().required(),
    totalIntervals: Yup.string(),
    dimension: Yup.object({
        department: Yup.number().nullable(),
        isDepartmentMandatory: Yup.boolean(),
        isUnitMandatory: Yup.boolean(),
        unit1: Yup.number().nullable(),
        unit2: Yup.number().nullable(),
    }).nullable(),
    currency: Yup.string(),
});

const getUpdateValue = (key, type, allValues) => (isFieldDisabled(key, { type }) ? undefined : allValues[key]);

const updater = [
    {
        field: 'type',
        updates: (type, _, allValues: any = {}) => {
            return {
                'accounts.contraAccount': getUpdateValue('accounts.contraAccount', type, allValues),
                'accounts.openingAccount': getUpdateValue('accounts.openingAccount', type, allValues),
                'accounts.totalFromAccount': getUpdateValue('accounts.totalFromAccount', type, allValues),
                'accounts.realisationAccount': getUpdateValue('accounts.realisationAccount', type, allValues),
                currency: getUpdateValue('currency', type, allValues),
                vatCode: getUpdateValue('vatCode', type, allValues),
            };
        },
    },
];

enum CreateAccountTab {
    General = 'tab-general',
    Dimension = 'tab-dimension',
}
interface AccountProps extends FormProps<Account> {
    modalDraggable?: boolean;
}
export default function CreateAccount({
    initialData = initialValues,
    notifications,
    onSubmitted,
    onSubmitError,
    onCancel,
    modalDraggable,
}: AccountProps) {
    const agreement = useCurrentAgreement();
    const hasDimension = useDimensionsAccessLevel(DimensionsAccess.HasDimension);
    const hasAccrualModule = agreement.modules.accruals || agreement.modules.fixedAssetsAndAccruals;
    const hasMultipleDimensions = useDimensionsAccessLevel(DimensionsAccess.HasMultiDimensions);
    const dimensionsDefinitionsQuery = useQuery<Dimension[]>(
        `/dimension/GetAll?includeDistributionKeys=${true}&active=${true}`,
        undefined,
        {
            skip: !hasMultipleDimensions,
        }
    );

    const handleSubmit = async values => {
        try {
            const response = await internalApiClient.post(`/accounts`, values);
            return await Promise.resolve(response);
        } catch (error) {
            error.validation = error.response.data;
            return Promise.reject(error);
        }
    };

    return (
        <Form
            initialData={initialData}
            messages={notifications}
            onSubmit={handleSubmit}
            onSubmitted={onSubmitted}
            onSubmitError={onSubmitError}
            updates={updater}
            validationSchema={validationSchema}>
            {({ values }) => {
                const defaultFields = (
                    <Row divided>
                        <Column xs="3">
                            <Field.Dropdown
                                label={t`Type`}
                                name="type"
                                as={AccountTypeDropdown}
                                required
                                includeAllTypesButSumAlpha
                            />
                            <Field.Number label={t`No.`} name="number" required />
                            <Field.Text label={t`Name`} name="name" required />
                        </Column>

                        <Column xs="3">
                            <Field.Dropdown
                                label={t`VAT`}
                                name="vatCode"
                                disabled={isFieldDisabled('vatCode', values)}
                                as={VatCodeDropdown}
                                emptyValue={null}
                            />
                            <Field.Combobox
                                label={t`Contra account`}
                                name="accounts.contraAccount"
                                disabled={isFieldDisabled('accounts.contraAccount', values)}
                                as={AccountComboBox}
                            />

                            <Field.Combobox
                                label={t`Total from account`}
                                name="accounts.totalFromAccount"
                                disabled={isFieldDisabled('accounts.totalFromAccount', values)}
                                as={AccountComboBox}
                            />
                        </Column>

                        <Column xs="3">
                            <Field.Combobox
                                label={<Trans context="Primokonto">Opening account</Trans>}
                                name="accounts.openingAccount"
                                disabled={isFieldDisabled('accounts.openingAccount', values)}
                                as={AccountComboBox}
                            />
                            {hasAccrualModule && (
                                <Field.Combobox
                                    label={t`Realisation account`}
                                    name="accounts.realisationAccount"
                                    disabled={isFieldDisabled('accounts.realisationAccount', values)}
                                    as={AccountComboBox}
                                />
                            )}
                            <Field.Dropdown
                                label={t`Key figure code`}
                                name="keyFigureCode"
                                disabled={isFieldDisabled('keyFigureCode', values)}
                                as={KeyFigureCodeDropdown}
                                emptyValue={null}
                            />
                        </Column>

                        <Column xs="3">
                            <Row>
                                <Column xs="6">
                                    <Field.Dropdown
                                        label={t`Debit/Credit`}
                                        name="debitCredit"
                                        disabled={isFieldDisabled('debitCredit', values)}
                                        as={DebitCreditDropdown}
                                    />
                                </Column>
                                <Column xs="6">
                                    <Field.Dropdown
                                        label={tLegacy`FORM_FIELD_LABEL_ACCESS`}
                                        name="access"
                                        disabled={isFieldDisabled('access', values)}
                                        as={AccessDropdown}
                                    />
                                </Column>
                            </Row>
                            <Row>
                                <Column>
                                    <Field.Checkbox
                                        text={t`Block direct entries`}
                                        name="isBlockedForDirectEntries"
                                        disabled={isFieldDisabled('isBlockedForDirectEntries', values)}
                                    />

                                    <Condition for="type" when={type => type === 1 || type === 2}>
                                        <Row>
                                            <Column xs="6">
                                                <Field.Dropdown label={t`Currency`} name="currency" as={CurrencyDropdown} />
                                            </Column>
                                        </Row>
                                    </Condition>

                                    <Condition for="type" when={type => type === 6}>
                                        <Field.Text
                                            label={t`Total intervals`}
                                            name="totalIntervals"
                                            disabled={isFieldDisabled('totalIntervals', values)}
                                        />
                                    </Condition>
                                </Column>
                            </Row>
                        </Column>
                    </Row>
                );

                return (
                    <>
                        <h1>{t`New account`}</h1>

                        {hasDimension ? (
                            <Tabs defaultId={CreateAccountTab.General}>
                                <Tabs.List>
                                    <Tabs.Trigger
                                        id={
                                            CreateAccountTab.General
                                        }>{tLegacy`FIREBIRD_FORM_CUSTOMERS_TAB_GENERAL`}</Tabs.Trigger>
                                    <Tabs.Trigger id={CreateAccountTab.Dimension}>{t`Dimension`}</Tabs.Trigger>
                                </Tabs.List>
                                <Tabs.Content id={CreateAccountTab.General}>{defaultFields}</Tabs.Content>
                                <Tabs.Content id={CreateAccountTab.Dimension}>
                                    <Row divided>
                                        {!hasMultipleDimensions && (
                                            <Column xs="3">
                                                <Field.Combobox
                                                    label={t`Department no.`}
                                                    name="dimension.department"
                                                    modalDraggable={modalDraggable}
                                                    as={DepartmentCombobox}
                                                />
                                                <Field.Checkbox
                                                    text={t`Mandatory department`}
                                                    name="dimension.isDepartmentMandatory"
                                                />
                                            </Column>
                                        )}
                                        {hasMultipleDimensions && dimensionsDefinitionsQuery?.data && (
                                            <Column xs="6">
                                                <div>
                                                    <div className="flex flex-col gap-2 mb-2">
                                                        {dimensionsDefinitionsQuery.data?.map((dimensionDefinition, index) => {
                                                            return (
                                                                <Group
                                                                    key={dimensionDefinition.id}
                                                                    className="w-full flex flex-row justify-between mb-2">
                                                                    <Field.Hidden
                                                                        name={`dimensions[${index}].dimensionId`}
                                                                        initialValue={dimensionDefinition.id}
                                                                    />
                                                                    <Field.Combobox
                                                                        name={`dimensions[${index}].dimensionKey`}
                                                                        dimensionId={dimensionDefinition.id}
                                                                        fieldClassName="flex flex-1 flex-col -mb-4"
                                                                        label={dimensionDefinition.name}
                                                                        as={DimensionLineCombobox}
                                                                        disabled={false}
                                                                    />
                                                                    <Field.Checkbox
                                                                        text={t`Mandatory Dimension`}
                                                                        name={`dimensions[${index}].isMandatory`}
                                                                    />
                                                                </Group>
                                                            );
                                                        })}
                                                    </div>
                                                </div>
                                            </Column>
                                        )}
                                        <Column xs="3">
                                            <Field.Checkbox text={t`Mandatory unit`} name="dimension.isUnitMandatory" />
                                            <Field.Dropdown
                                                label={t`Unit`}
                                                name="dimension.unit1"
                                                as={UnitDropdown}
                                                emptyValue={null}
                                            />
                                            <Field.Dropdown
                                                label={t`Unit 2`}
                                                name="dimension.unit2"
                                                as={UnitDropdown}
                                                emptyValue={null}
                                            />
                                        </Column>
                                    </Row>
                                </Tabs.Content>
                            </Tabs>
                        ) : (
                            defaultFields
                        )}

                        <div className="form__buttons">
                            <Button type="submit" appearance="primary">
                                <Trans>Save</Trans>
                            </Button>
                            {onCancel && (
                                <Button onClick={onCancel} appearance="default">
                                    <Trans>Cancel</Trans>
                                </Button>
                            )}
                        </div>
                    </>
                );
            }}
        </Form>
    );
}
